import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const slides = [
  {
    id: 1,
    background: 'https://images.unsplash.com/photo-1650397373572-d9329e4328ee?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    overlay: 'bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40'
  },
  {
    id: 2, 
    background:  'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    overlay: 'bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40'
  },
  {
    id: 3,
    background: 'https://images.unsplash.com/39/lIZrwvbeRuuzqOoWJUEn_Photoaday_CSD%20%281%20of%201%29-5.jpg?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    overlay: 'bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40'
  },
  {
    id: 4,
    background: 'https://images.unsplash.com/photo-1639164757215-491a7c76a91d?q=80&w=1740&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
    overlay: 'bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/40'
  }
];

export const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { t, isRTL } = useLanguage();

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const slideContent = [
    {
      headline: t('hero.slide1.headline'),
      subheadline: t('hero.slide1.subheadline'),
      claim: t('hero.slide1.claim'),
      cta: t('hero.slide1.cta')
    },
    {
      headline: t('hero.slide2.headline'),
      subheadline: t('hero.slide2.subheadline'),
      cta: t('hero.slide2.cta')
    },
    {
      headline: t('hero.slide3.headline'),
      subheadline: t('hero.slide3.subheadline'),
      cta: t('hero.slide3.cta')
    },
    {
      headline: t('hero.slide4.headline'),
      subheadline: t('hero.slide4.subheadline'),
      cta: t('hero.slide4.cta')
    }
  ];

  return (
    <section id="home" className="relative h-screen overflow-hidden">
      {/* Background Images */}
      {slides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={slide.background}
            alt={`Slide ${index + 1}`}
            className="w-full h-full object-cover"
          />
          <div className={`absolute inset-0 ${slide.overlay}`} />
        </div>
      ))}

      {/* Creative Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-10 w-72 h-72 bg-gradient-to-r from-purple-600/20 to-orange-500/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-1/4 -right-10 w-96 h-96 bg-gradient-to-r from-orange-500/20 to-purple-600/20 rounded-full blur-3xl animate-float" style={{ animationDelay: '-3s' }} />
      </div>

      {/* Content - Responsive with proper padding and RTL support */}
      <div className="relative z-10 h-full flex items-center pt-28 pb-16 md:pt-24 md:pb-20">
        <div className={`max-w-7xl mx-auto px-6 lg:px-8 w-full ${isRTL ? 'text-right' : 'text-left'}`}>
          <div className="max-w-4xl">
            {slideContent.map((content, index) => (
              <div
                key={index}
                className={`transition-all duration-700 ${
                  index === currentSlide 
                    ? 'opacity-100 translate-y-0' 
                    : 'opacity-0 translate-y-8'
                }`}
                style={{ display: index === currentSlide ? 'block' : 'none' }}
              >
                <h1 className={`text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-light text-white mb-4 md:mb-6 lg:mb-8 leading-tight tracking-tight ${isRTL ? 'font-medium' : ''}`}>
                  <span className="gradient-text font-medium">
                    {content.headline.split(' ').slice(0, 2).join(' ')}
                  </span>
                  <br />
                  {content.headline.split(' ').slice(2).join(' ')}
                </h1>
                
                <div className="space-y-4 md:space-y-6 mb-8 md:mb-12">
                  <p className={`text-lg sm:text-xl lg:text-2xl text-white/90 leading-relaxed max-w-4xl font-light ${isRTL ? 'font-normal' : ''}`}>
                    {content.subheadline}
                  </p>
                  
                  {content.claim && (
                    <p className={`text-base sm:text-lg text-orange-400 font-medium italic max-w-3xl ${isRTL ? 'font-semibold not-italic' : ''}`}>
                      {content.claim}
                    </p>
                  )}
                </div>

                <button className="group inline-flex items-center px-6 md:px-8 py-3 md:py-4 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium text-base md:text-lg transition-all duration-300 hover:shadow-glow hover:scale-105">
                  <span className={isRTL ? 'ml-3' : 'mr-3'}>
                    {content.cta}
                  </span>
                  <ArrowRight className={`w-4 h-4 md:w-5 md:h-5 transition-transform duration-300 group-hover:translate-x-1 ${isRTL ? 'rotate-180 group-hover:-translate-x-1' : ''}`} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Elegant Navigation Controls */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex items-center space-x-6 bg-white/10 backdrop-blur-xl rounded-full px-6 py-3 border border-white/20">
          <button
            onClick={prevSlide}
            className="p-2 text-white hover:text-orange-400 transition-colors duration-200"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          
          <div className="flex space-x-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  index === currentSlide 
                    ? 'bg-gradient-to-r from-purple-600 to-orange-500 scale-125' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
          
          <button
            onClick={nextSlide}
            className="p-2 text-white hover:text-orange-400 transition-colors duration-200"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </section>
  );
};